import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router'

import { cloneDeep, isEmpty, merge, pick } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import { css } from 'styled-components/native'

import { extendContentMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/contentSchemaUtils'
import { getInitialICD10s } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { ICD10_PRIMARY_FIELD, ICD10_SECONDARY_FIELD } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/constants'
import { Transfer } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import ContentLayout from '@lyrahealth-inc/ui-core/lib/molecules/layouts/base/ContentLayout'
import CardMedium from '@lyrahealth-inc/ui-core/lib/molecules/layouts/cardMedium/CardMedium'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { FormBody, FormButtonParams } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import attestationFormMetadata from './attestationFormMetadata'
import getApproachingSpecialityCareMetadata from './getApproachingSpecialityCareMetadata'
import getPreAuthSpecialityCareMetadata from './getPreAuthSpecialityCareMetadata'
import ICD10sField from './ICD10sField'
import ICD10sFormMetadata from './ICD10sFormMetadata'
import { addAlert, hideAlerts } from '../../../data/alertActions'
import { getAuthRoles } from '../../../data/auth/authSelectors'
import { getHealthPlanCurrentCharge, getHealthPlanICD10s } from '../../../data/healthPlan/healthPlanSelectors'
import { clearHealthPlanCharge, clearHPEligibility, getICD10s } from '../../../data/healthPlanAutoActions'
import { getPaymentsDataAllData } from '../../../data/payments/paymentsSelectors'
import { getRequestPaymentCustomers } from '../../../data/requestPayment/requestPaymentSelectors'
import { RootState } from '../../../data/store'
import { submitPaymentRequest } from '../../../requestPayment/data/requestPaymentActions'
import { bannerMessages, ROLES, specialityCareAttestationStatuses } from '../../constants/appConstants'
import {
  CLIENT_EMAIL,
  PRACTICES_DETAILS_HEALTHPLAN_PAYMENT_INFO,
  PRACTICES_DETAILS_NEW_PAYMENT,
  PROVIDERS_DETAILS_HEALTHPLAN_PAYMENT_INFO,
  PROVIDERS_DETAILS_NEW_PAYMENT,
  REQUEST_PAYMENT,
} from '../../constants/routingConstants'
import { convertToFormData, hasRole, prepopulateAttestation } from '../../utils/utils'

export const HealthPlanInfoAdmins: React.FC<HealthPlanInfoProps> = ({
  charge,
  availableCodes,
  customers = [],
  pastPayments,
  actions: { getICD10s, hideAlerts, addAlert, clearHealthPlanCharge, clearHPEligibility, submitPaymentRequest },
}) => {
  const intl = useIntl()
  const roles = useSelector(getAuthRoles)
  const [loading] = useFetcher([getICD10s, {}])
  const navigate = useNavigate()
  const location = useLocation()
  const hasClearedCharge = useRef(false)
  const initialICD10s = useMemo(() => getInitialICD10s(availableCodes, charge), [availableCodes, charge])

  const goToNextScreen = useCallback(
    (
      chargeSubmitted,
      charge: Transfer & { session_tracking_status?: string; email_required?: boolean; data?: { company?: string } },
    ) => {
      if (charge?.email_required && !hasRole(roles, ROLES.PAYMENTS_ADMIN) && chargeSubmitted) {
        navigate(CLIENT_EMAIL.route, {
          state: {
            patient_id: charge?.patient_lyra_id,
            initialSession: charge?.session_tracking_status === 'session_limit_initial_session',
            sessionsLeft: parseInt(charge?.sessions_remaining, 10),
            firstName: charge?.first_name,
            lastName: charge?.last_name,
            customer: customers.find((customer: CustomerInfo) => customer.value === charge?.data?.company),
          },
        })
      } else if (
        hasRole(roles, ROLES.PAYMENTS_ADMIN) &&
        location.pathname === PRACTICES_DETAILS_HEALTHPLAN_PAYMENT_INFO.route
      ) {
        navigate(PRACTICES_DETAILS_NEW_PAYMENT.route, { replace: true, state: { prev_route: location.pathname } })
      } else if (
        hasRole(roles, ROLES.PAYMENTS_ADMIN) &&
        location.pathname === PROVIDERS_DETAILS_HEALTHPLAN_PAYMENT_INFO.route
      ) {
        navigate(PROVIDERS_DETAILS_NEW_PAYMENT.route, {
          replace: true,
          state: {
            provider_id: charge?.provider_lyra_id,
            prev_route: location.pathname,
          },
        })
      } else {
        navigate(REQUEST_PAYMENT.route, { replace: true, state: { prev_route: location.pathname } })
      }
    },
    [customers, location.pathname, navigate, roles],
  )

  const submitForm = useCallback(
    (values: { values?: Dict }) => {
      hideAlerts()
      const formValues = { ...values.values }
      if (Array.isArray(formValues[ICD10_PRIMARY_FIELD])) {
        formValues[ICD10_PRIMARY_FIELD] = formValues[ICD10_PRIMARY_FIELD].map((obj: any) => obj.code).join(',')
      }
      if (Array.isArray(formValues[ICD10_SECONDARY_FIELD])) {
        formValues[ICD10_SECONDARY_FIELD] = formValues[ICD10_SECONDARY_FIELD].map((obj: any) => obj.code).join(',')
      }
      const mergeChargeHPIData = {
        ...charge,
        ...formValues,
      }
      return submitPaymentRequest(convertToFormData(mergeChargeHPIData), false).then(
        (rpReturn: any) => {
          const chargeCopy = cloneDeep(charge)
          chargeCopy.session_tracking_status = rpReturn.data.session_tracking_status
          chargeCopy.sessions_remaining = rpReturn.data.data.sessions_remaining
          chargeCopy.email_required = rpReturn.data.email_required
          addAlert({
            show: true,
            contents: bannerMessages.SUBMIT_PAYMENT_SUCCESS,
            style: 'success',
            expires: false,
            autoDismissTimer: 10000,
          })
          hasClearedCharge.current = true
          clearHealthPlanCharge()
          clearHPEligibility()
          goToNextScreen(true, chargeCopy)
        },
        (error: any) => {
          const errorType = error.response.data.type
          if (errorType === 'duplicate_payment' || errorType === 'cancellation_policy') {
            goToNextScreen(false, charge)
          } else {
            const errorMessage = error.response.data.message ?? ''
            addAlert({
              show: true,
              contents:
                typeof errorMessage === 'string' && errorMessage.includes('No episodes')
                  ? 'Unable to submit: No active care episode found for this provider and patient combination.'
                  : errorMessage || 'An error occurred while submitting the payment request.',
              style: 'danger',
              expires: false,
            })
          }
        },
      )
    },
    [addAlert, charge, clearHealthPlanCharge, clearHPEligibility, goToNextScreen, hideAlerts, submitPaymentRequest],
  )

  const getAttended = () => pick(charge, 'attendance')

  const submitButton = useCallback(({ handleSubmit }: FormButtonParams) => {
    return <PrimaryButton text={'Submit'} onPress={handleSubmit} testID={tID('HealthPlanInfoAdmins-submit')} />
  }, [])

  if (isEmpty(charge)) {
    if (hasClearedCharge.current) {
      return null
    }
    return <Navigate to={REQUEST_PAYMENT.route} />
  }

  const { next_session_uses_health_plan, speciality_care_attestation_status, session_type } = charge
  const isDBTGroupSession = session_type === LTSessionTypes.DBT_GROUP_SESSION
  const shouldShowICD10 = next_session_uses_health_plan || isDBTGroupSession

  let metadata: Content = {
    meta_data: { schema: { type: 'object', properties: {} }, uiSchema: { 'ui:order': [] } },
    content_type: 'form',
    group: 'exercise',
    name: 'healthPlanInfoMetadata',
    title: '',
  }

  if (shouldShowICD10) {
    metadata = extendContentMetadata(metadata, ICD10sFormMetadata)
    ;(metadata.meta_data as any).initialValues = merge(getAttended(), initialICD10s)
  }
  const { APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED } =
    specialityCareAttestationStatuses
  const { label: companyLabel } =
    customers.find(({ value }) =>
      value === (Array.isArray(charge?.company) && charge?.company[0].value) ? charge?.company[0].value : undefined,
    ) || {}
  switch (speciality_care_attestation_status) {
    case APPROACHING_ATTESTATION:
      metadata = extendContentMetadata(
        metadata,
        getApproachingSpecialityCareMetadata(companyLabel, `${charge?.first_name} ${charge?.last_name}`),
      )
      break
    case ATTESTATION_PRIOR_AUTHORIZATION:
      metadata = extendContentMetadata(
        metadata,
        getPreAuthSpecialityCareMetadata(
          companyLabel,
          charge?.first_name,
          `${charge?.first_name} ${charge?.last_name}`,
        ),
      )
      break
    case ATTESTATION_NEEDED:
      metadata = extendContentMetadata(metadata, attestationFormMetadata)
      ;(metadata.meta_data as any).initialValues = merge(
        getAttended(),
        initialICD10s,
        prepopulateAttestation(pastPayments),
      )
      break
    default:
      break
  }

  return (
    <ContentLayout>
      <CardMedium backText={'Request Payment'} backFunc={() => goToNextScreen(false, charge)}>
        {loading || isEmpty(availableCodes) ? (
          <div style={{ textAlign: 'center' }}>
            <LoadingIndicator size={45} />
          </div>
        ) : (
          <FormBody
            intl={intl}
            schema={metadata?.meta_data?.schema}
            uiSchema={metadata?.meta_data?.uiSchema}
            customFields={{ icd10s: (props: any) => <ICD10sField {...props} availableCodes={availableCodes} /> }}
            name={metadata?.name}
            formButton={submitButton}
            saveForm={submitForm}
            withPageBreaks={false}
            formBodyCustomStyles={{
              submitButtonContainer: css`
                border-top-width: 0;
                padding: 0;
                width: 100%;
                box-shadow: none;
                border-bottom-left-radius: 16px;
                border-bottom-right-radius: 16px;
              `,
              submitButtonWrapper: css`
                max-width: none;
                margin-left: 40px;
                margin-bottom: 20px;
              `,
            }}
            initialValues={{
              ...(shouldShowICD10 && merge(getAttended(), initialICD10s)),
              ...(speciality_care_attestation_status === ATTESTATION_NEEDED && prepopulateAttestation(pastPayments)),
            }}
          />
        )}
      </CardMedium>
    </ContentLayout>
  )
}

type HealthPlanInfoProps = {
  actions: any
  charge: any
  availableCodes: any
  customers: Array<any>
  pastPayments: Array<any>
}

const mapStateToProps = (state: RootState) => {
  return {
    charge: getHealthPlanCurrentCharge(state),
    availableCodes: getHealthPlanICD10s(state),
    customers: getRequestPaymentCustomers(state),
    pastPayments: getPaymentsDataAllData(state),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: bindActionCreators(
    {
      clearHealthPlanCharge,
      clearHPEligibility,
      submitPaymentRequest,
      getICD10s,
      hideAlerts,
      addAlert,
    },
    dispatch,
  ),
})

export default connect(mapStateToProps, mapDispatchToProps)(HealthPlanInfoAdmins)
